import { useCallback, useMemo, useState } from "react";

const defaultGetId = (item) => item?.id;

const useRowSelection = (items, getId = defaultGetId) => {
  const [selectedIds, setSelectedIds] = useState([]);

  const visibleIds = useMemo(
    () => items.map((item) => getId(item)).filter((id) => id !== undefined && id !== null),
    [getId, items],
  );

  const isAllSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleSelection = useCallback((id) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id],
    );
  }, []);

  const toggleAllVisible = useCallback(() => {
    setSelectedIds((current) => {
      const hasAllVisible =
        visibleIds.length > 0 && visibleIds.every((id) => current.includes(id));

      if (hasAllVisible) {
        return current.filter((id) => !visibleIds.includes(id));
      }

      const merged = [...current];
      visibleIds.forEach((id) => {
        if (!merged.includes(id)) {
          merged.push(id);
        }
      });

      return merged;
    });
  }, [visibleIds]);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  return useMemo(
    () => ({
      selectedIds,
      setSelectedIds,
      isAllSelected,
      toggleSelection,
      toggleAllVisible,
      clearSelection,
    }),
    [
      selectedIds,
      setSelectedIds,
      isAllSelected,
      toggleSelection,
      toggleAllVisible,
      clearSelection,
    ],
  );
};

export default useRowSelection;
